import { Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { omit } from 'lodash';
import { useEffect, useState } from 'react';
import { createDownLoad, updateDownLoad } from '../../../api';
import { ConsoleDrawer } from '../../../components/drawer';
import { ConsoleForm } from '../../../components/form';
import {
  SelectViewField,
  TextViewField,
} from '../../../components/form/view-field';
import { CommonInput, CommonTextArea } from '../../../components/input';
import { messageError } from '../../../components/message';
import { CommonSelect } from '../../../components/select';
import {
  getFirstManageSite,
  getLangFormatText,
  getLangFormatLink,
  getNeedManageSites,
  LanguageType,
} from '../../../lib';
import getFormatMessage from '../../../locales/get-format-message';
import {
  CommonStatusStep,
  DownloadObjectType,
  IDownLoadMenu,
  IDownLoadType,
} from '../../../model/api-modules';
import { MultiSiteStatus } from '../components/multi-site-status';
import { shortHash10 } from '../lib/hash';
import { ISiteIDownLoadMenu } from './download-items';
import { getTypeOptions } from './lib/download-form';
import {
  handleMultiSiteSubmitRes,
  IMultiSubmitRes,
  multiSiteSubmitter,
} from './lib/multi-site-helper';
import { AutoTooltip } from '../../../components/auto-tooltip';
import questionIcon from '../../../assets/svg/question-icon.svg';

interface IProps {
  readonly onClose: (status?: boolean) => void;
  readonly row: IDownLoadMenu | null;
  readonly type: IDownLoadType;
  readonly allSiteDownLoadList: ISiteIDownLoadMenu;
}

export const DownLoadDrawer = (props: IProps) => {
  const { allSiteDownLoadList, row, type } = props;
  const [form] = useForm<any>();
  // const [error, setError] = useState<boolean>(false);
  const [init, setInit] = useState<boolean>(false);
  const [downloadLevel, setDownloadLevel] = useState<number | null>(null);
  const [downloadType, setDownloadType] = useState<number | null>(null);

  const getLevelOptions = () => {
    return [
      {
        label: '一级下载物',
        value: 1,
      },
      {
        label: '二级下载物',
        value: 2,
        disabled: downloadType !== DownloadObjectType.Client,
      },
    ];
  };

  const getFirstDownload = () => {
    const currentDownloadList = allSiteDownLoadList[getFirstManageSite()];
    const firstList = currentDownloadList.filter((item) => {
      return !item.ParentTag;
    });
    const firstOptions = firstList.map((item) => {
      return {
        label: item.Name,
        value: item.Tag,
      };
    });
    return firstOptions;
  };

  const getSecondDownload = () => {
    const currentDownloadList = allSiteDownLoadList[getFirstManageSite()];
    const secondList = currentDownloadList.filter((item) => {
      return !!item.ParentTag && !!row?.Tag && item.ParentTag === row.Tag;
    });
    return secondList;
  };

  const getModalTitle = () => {
    if (props.type === IDownLoadType.Create) {
      return getFormatMessage('create_download');
    }
    if (props.type === IDownLoadType.View) {
      return getFormatMessage('查看下载');
    }
    return getFormatMessage('edit_download');
  };
  const getSuccessMessage = () => {
    return props.type === IDownLoadType.Create
      ? getFormatMessage('create_success')
      : getFormatMessage('edit_success');
  };
  const handleOpenCancel = () => {
    form.resetFields();
    setDownloadLevel(null);
    props.onClose();
  };
  const handleOpenSubmit = async () => {
    const values = await form.validateFields();

    if (!values) {
      return false;
    }

    const currentDownloadList = allSiteDownLoadList[getFirstManageSite()];
    if (
      props.type === IDownLoadType.Create &&
      currentDownloadList.some((item) => item.Name === values.Name)
    ) {
      messageError('名称重复,请重新输入');
      return false;
    }
    try {
      const trimmedName = values.Name.trim();
      const valuesForm: any = {
        ...omit(values, 'SiteStatus'),
        Name: trimmedName,
        ParentTag: values.ParentTag || null,
        Description: JSON.stringify({
          zh_CN: values.zhDescription || '',
          en_US: values.enDescription || '',
        }),
        DocLink: JSON.stringify({
          zh_CN: values.DocLinkCn || '',
          en_US: values.DocLinkEn || '',
        }),
      };
      valuesForm.zhDescription = undefined;
      valuesForm.enDescription = undefined;
      valuesForm.DocLinkCn = undefined;
      valuesForm.DocLinkEn = undefined;
      valuesForm.DownloadLevel = undefined;

      const updatedValues = Object.assign({}, valuesForm);
      delete updatedValues.Type;
      delete updatedValues.Tag;
      delete updatedValues.ParentTag;
      let res: IMultiSubmitRes = [];
      if (props.type === IDownLoadType.Edit && props.row) {
        res = await multiSiteSubmitter(
          (matchRow: IDownLoadMenu, site) => {
            return updateDownLoad(matchRow.id, {
              ...updatedValues,
              Status: values.SiteStatus[site],
            });
          },
          (site) =>
            allSiteDownLoadList[site]?.find(
              (item) => item.Tag === props.row?.Tag
            ),
          async (site) => {
            const dataSyncID = await shortHash10(values.Name);
            return createDownLoad({
              ...valuesForm,
              Status: values.SiteStatus[site],
              DataSyncID: dataSyncID,
              DataSource: 'support',
            });
          }
        );
      } else if (props.type === IDownLoadType.Create) {
        const dataSyncID = await shortHash10(values.Name);
        res = await multiSiteSubmitter((_, site) =>
          createDownLoad({
            ...valuesForm,
            Status: values.SiteStatus[site],
            DataSyncID: dataSyncID,
            DataSource: 'support',
          })
        );
      }
      return handleMultiSiteSubmitRes(res, {
        onSuccess: () => {
          form.resetFields();
          props.onClose(true);
        },
        successMessage: getSuccessMessage(),
      });
    } catch (error: any) {
      messageError(
        error?.apiError?.message || getFormatMessage('failed_submit')
      );
      return false;
    }
  };
  const isViewOnly = type === IDownLoadType.View;

  useEffect(() => {
    if (props.row) {
      const formatDesc = getLangFormatText(props.row?.Description);
      const formatDocLink = getLangFormatLink(props.row?.DocLink);
      form.setFieldsValue({
        ...props.row,
        zhDescription: formatDesc[LanguageType.zh_CN],
        enDescription: formatDesc[LanguageType.en_US],
        DocLinkCn: formatDocLink[LanguageType.zh_CN],
        DocLinkEn: formatDocLink[LanguageType.en_US],
        DownloadLevel: props.row.ParentTag ? 2 : 1,
        SiteStatus:
          row &&
          getNeedManageSites().reduce<Record<string, CommonStatusStep>>(
            (acc, site) => {
              const matchStatus = allSiteDownLoadList[site].find(
                (item) => item.Name === row.Name
              )?.Status;
              if (matchStatus !== undefined) {
                acc[site] = matchStatus;
              }
              return acc;
            },
            {}
          ),
      });
      setDownloadType(props.row.Type);
      if (props.row.ParentTag) {
        setDownloadLevel(2);
      } else {
        setDownloadLevel(1);
      }
    }
    setInit(true);
  }, [props.type]);

  const changeDownloadType = (e: number) => {
    setDownloadType(e);
    if (e !== DownloadObjectType.Client) {
      form.setFieldsValue({
        DownloadLevel: 1,
        ParentTag: '',
      });
      setDownloadLevel(1);
    }
  };

  const renderFormItems = () => {
    const { row } = props;
    const secondList = getSecondDownload();
    if (isViewOnly && row) {
      return (
        <>
          <label>基础信息</label>

          <TextViewField name="Name" label="下载物名称" />

          <TextViewField name="Tag" label="下载物标识" />

          <SelectViewField
            label={getFormatMessage('type_uppercase')}
            name="Type"
            options={getTypeOptions()}
          />

          <SelectViewField
            label="下载物级别"
            name="DownloadLevel"
            options={getLevelOptions()}
          />

          {downloadLevel === 2 && (
            <SelectViewField
              label="关联一级下载物"
              name="ParentTag"
              options={getFirstDownload()}
            />
          )}

          <TextViewField
            name="zhDescription"
            label={getFormatMessage('zh_description')}
          />

          <TextViewField
            name="enDescription"
            label={getFormatMessage('en_description')}
          />

          <TextViewField name="DocLinkCn" label="中文文档链接" />

          <TextViewField name="DocLinkEn" label="英文文档链接" />

          <Form.Item label={getFormatMessage('启用状态配置')} name="SiteStatus">
            <MultiSiteStatus viewOnly />
          </Form.Item>

          {props.type === IDownLoadType.Edit &&
            !!row?.Tag &&
            downloadLevel === 1 &&
            secondList.length !== 0 && (
              <Form.Item label="可选二级下载物">
                {secondList.map((item) => {
                  return (
                    <div style={{ border: '1px solid #000000D9' }}>
                      {item.Name}
                    </div>
                  );
                })}
              </Form.Item>
            )}
        </>
      );
    }
    return (
      <>
        <label>基础信息</label>

        <Form.Item
          name="Name"
          label={
            <div style={{ position: 'relative' }}>
              <span>下载物名称</span>
              <AutoTooltip title="下载中心展示所用下载物名称" placement="top">
                <img
                  src={questionIcon}
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    position: 'absolute',
                    top: '5px',
                    left: '80px',
                  }}
                />
              </AutoTooltip>
            </div>
          }
          rules={[{ required: true }]}
        >
          <CommonInput
            placeholder={getFormatMessage('please_enter_download_name')}
            fullWidth
          />
        </Form.Item>

        <Form.Item
          name="Tag"
          label={
            <div style={{ position: 'relative' }}>
              <span>下载物标识</span>
              <AutoTooltip
                title="下载物对应唯一标识，创建后不可修改"
                placement="top"
              >
                <img
                  src={questionIcon}
                  style={{
                    display: 'inline-block',
                    width: 12,
                    height: 12,
                    position: 'absolute',
                    top: '5px',
                    left: '80px',
                  }}
                />
              </AutoTooltip>
            </div>
          }
          rules={[
            { required: true },
            { pattern: /^[A-Za-z]*$/, message: '只能输入英文字母' },
          ]}
        >
          <CommonInput
            placeholder="输入下载物标识"
            fullWidth
            disabled={props.type === IDownLoadType.Edit && !!row?.Tag}
          />
        </Form.Item>

        <Form.Item
          name="Type"
          label={getFormatMessage('type_uppercase')}
          rules={[{ required: true }]}
        >
          <CommonSelect
            options={getTypeOptions()}
            placeholder={getFormatMessage('please_select_download_type')}
            disabled={props.type === IDownLoadType.Edit}
            onChange={(e) => changeDownloadType(e)}
          ></CommonSelect>
        </Form.Item>

        <Form.Item
          name="DownloadLevel"
          label="下载物级别"
          rules={[{ required: true }]}
        >
          <CommonSelect
            options={getLevelOptions()}
            placeholder="请选择下载物级别"
            disabled={props.type === IDownLoadType.Edit}
            onChange={(e) => setDownloadLevel(e)}
          ></CommonSelect>
        </Form.Item>

        {downloadLevel === 2 && (
          <Form.Item
            name="ParentTag"
            label="关联一级下载物"
            rules={[{ required: true }]}
          >
            <CommonSelect
              options={getFirstDownload()}
              placeholder="请选择关联一级下载物"
              disabled={props.type === IDownLoadType.Edit}
            ></CommonSelect>
          </Form.Item>
        )}

        <Form.Item
          name="zhDescription"
          label={getFormatMessage('zh_description')}
          rules={[{ required: true }]}
        >
          <CommonTextArea
            placeholder={getFormatMessage('please_enter_zh_desc')}
            style={{ height: '80px' }}
          />
        </Form.Item>

        <Form.Item
          name="enDescription"
          label={getFormatMessage('en_description')}
          rules={[{ required: true }]}
        >
          <CommonTextArea
            placeholder={getFormatMessage('please_enter_en_desc')}
            style={{ height: '80px' }}
          />
        </Form.Item>

        <Form.Item
          name="DocLinkCn"
          label="中文文档链接"
          rules={[{ required: true }]}
        >
          <CommonInput placeholder="输入下载物中文文档链接" fullWidth />
        </Form.Item>

        <Form.Item
          name="DocLinkEn"
          label="英文文档链接"
          rules={[{ required: true }]}
        >
          <CommonInput placeholder="输入下载物英文文档链接" fullWidth />
        </Form.Item>

        <Form.Item
          label={getFormatMessage('启用状态配置')}
          name="SiteStatus"
          rules={[
            {
              required: true,
              message: getFormatMessage('please_select_status'),
            },
          ]}
        >
          <MultiSiteStatus />
        </Form.Item>
        {downloadLevel === 1 && secondList.length !== 0 && (
          <Form.Item label="可选二级下载物">
            {secondList.map((item) => {
              return (
                <div
                  style={{
                    border: '1px solid #86909c',
                    marginBottom: '4px',
                    padding: '4px',
                    color: '#86909c',
                  }}
                >
                  {item.Name}
                </div>
              );
            })}
          </Form.Item>
        )}
      </>
    );
  };

  if (!init) return null;
  return (
    <ConsoleDrawer
      onClose={handleOpenCancel}
      title={getModalTitle()}
      onOk={handleOpenSubmit}
      okText={getFormatMessage('submit')}
      hideFooter={isViewOnly}
    >
      <ConsoleForm form={form}>{renderFormItems()}</ConsoleForm>
    </ConsoleDrawer>
  );
};
